import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { Users2, Building2, Loader2, RefreshCw, CheckCircle2, XCircle, Headphones, Clock, Palette } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DAW_COLORS = {
  "Ableton Live": "text-green-400",
  "FL Studio":    "text-orange-400",
  "Logic Pro":    "text-blue-400",
  "Cubase":       "text-purple-400",
};

// ── Artist Collab Tabelle ─────────────────────────────────────────────────────
function ArtistCollabTable({ token }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/collabs/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setItems(await res.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? items
    : filter === "active" ? items.filter(i => i.active)
    : items.filter(i => !i.active);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-lg overflow-hidden border border-white/[0.07]">
          {["all","active","inactive"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}>
              {f === "all" ? `Alle (${items.length})` : f === "active" ? `Aktiv (${items.filter(i=>i.active).length})` : `Inaktiv (${items.filter(i=>!i.active).length})`}
            </button>
          ))}
        </div>
        <button onClick={load} disabled={loading}
          className="p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors border border-white/[0.07]">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-zinc-600">
          <Loader2 size={18} className="animate-spin" /> Lade…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-600 text-sm">Keine Einträge</div>
      ) : (
        <div className="bg-zinc-900 border border-white/[0.05] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.05] text-zinc-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Artist</th>
                <th className="px-4 py-3 text-left">Headline</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">DAW</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Sucht</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Kontakt</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Datum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.03]">
              {filtered.map(item => (
                <tr key={item.auth0_id || item.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.artist_image
                        ? <img src={item.artist_image} alt={item.artist_name} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                        : <div className="w-7 h-7 rounded-lg bg-zinc-800 flex-shrink-0" />
                      }
                      <span className="text-white font-semibold text-xs truncate max-w-[120px]">{item.artist_name || "–"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-zinc-300 text-xs">{item.headline || "–"}</span>
                    {item.description && <span className="text-zinc-600 text-[10px] block mt-0.5 break-words">{item.description}</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {item.daw
                      ? <span className={`text-xs font-medium flex items-center gap-1 ${DAW_COLORS[item.daw] || "text-zinc-400"}`}><Headphones size={11} /> {item.daw}</span>
                      : <span className="text-zinc-700 text-xs">–</span>
                    }
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(item.looking_for || []).map(lf => (
                        <span key={lf} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/15">{lf}</span>
                      ))}
                      {!item.looking_for?.length && <span className="text-zinc-700 text-xs">–</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="text-[10px] text-zinc-500 space-y-0.5">
                      {item.contact?.email && <div>{item.contact.email}</div>}
                      {item.contact?.instagram && <div>{item.contact.instagram}</div>}
                      {item.contact?.telegram && <div>{item.contact.telegram}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {item.active
                      ? <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 size={11} /> Aktiv</span>
                      : <span className="flex items-center gap-1 text-zinc-600 text-xs"><XCircle size={11} /> Inaktiv</span>
                    }
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-zinc-600 text-xs">
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString("de-DE") : "–"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Label-Anfragen Tabelle ────────────────────────────────────────────────────
function LabelRequestTable({ token }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/label-requests/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setItems(await res.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? items
    : filter === "active" ? items.filter(i => i.active)
    : items.filter(i => !i.active);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-lg overflow-hidden border border-white/[0.07]">
          {["all","active","inactive"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}>
              {f === "all" ? `Alle (${items.length})` : f === "active" ? `Aktiv (${items.filter(i=>i.active).length})` : `Inaktiv (${items.filter(i=>!i.active).length})`}
            </button>
          ))}
        </div>
        <button onClick={load} disabled={loading}
          className="p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors border border-white/[0.07]">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-zinc-600">
          <Loader2 size={18} className="animate-spin" /> Lade…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-600 text-sm">Keine Label-Anfragen</div>
      ) : (
        <div className="bg-zinc-900 border border-white/[0.05] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.05] text-zinc-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Label</th>
                <th className="px-4 py-3 text-left">Headline</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Gesucht</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Stil / Deadline</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Kontakt</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Datum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.03]">
              {filtered.map(item => (
                <tr key={item.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.label_logo
                        ? <img src={item.label_logo} alt={item.label_name} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                        : <div className="w-7 h-7 rounded-lg bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                            <Building2 size={12} className="text-emerald-600" />
                          </div>
                      }
                      <span className="text-white font-semibold text-xs truncate max-w-[120px]">{item.label_name || "–"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-zinc-300 text-xs">{item.headline || "–"}</span>
                    {item.description && <span className="text-zinc-600 text-[10px] block mt-0.5 break-words">{item.description}</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(item.looking_for || []).map(lf => (
                        <span key={lf} className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/15">{lf}</span>
                      ))}
                      {!item.looking_for?.length && <span className="text-zinc-700 text-xs">–</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="text-[10px] text-zinc-500 space-y-0.5">
                      {item.style && <div className="flex items-center gap-1"><Palette size={9} /> {item.style}</div>}
                      {item.deadline && <div className="flex items-center gap-1"><Clock size={9} /> {item.deadline}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="text-[10px] text-zinc-500 space-y-0.5">
                      {item.contact?.email && <div>{item.contact.email}</div>}
                      {item.contact?.instagram && <div>{item.contact.instagram}</div>}
                      {item.contact?.telegram && <div>{item.contact.telegram}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {item.active
                      ? <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 size={11} /> Aktiv</span>
                      : <span className="flex items-center gap-1 text-zinc-600 text-xs"><XCircle size={11} /> Inaktiv</span>
                    }
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-zinc-600 text-xs">
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString("de-DE") : "–"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Haupt-Seite ───────────────────────────────────────────────────────────────
export default function CollabAdminPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("artists");

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2">
            <Users2 size={22} className="text-blue-400" /> Collab & Label-Anfragen
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Artist-Gesuche und Label-Aufträge im Überblick
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            onClick={() => setActiveTab("artists")}
            data-testid="admin-tab-artists"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all"
            style={activeTab === "artists"
              ? { background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", color: "#93c5fd" }
              : { color: "#52525b" }}>
            <Users2 size={13} /> Artist-Gesuche
          </button>
          <button
            onClick={() => setActiveTab("labels")}
            data-testid="admin-tab-labels"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all"
            style={activeTab === "labels"
              ? { background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)", color: "#34d399" }
              : { color: "#52525b" }}>
            <Building2 size={13} /> Label-Anfragen
          </button>
        </div>

        {activeTab === "artists" && <ArtistCollabTable token={token} />}
        {activeTab === "labels"  && <LabelRequestTable token={token} />}
      </div>
    </AdminLayout>
  );
}
